import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { MapLibreMapHost, useMapLibreLocationsAutoFit } from "@ryde/controls";
import {
  GlobalAppEventEnum,
  ILocationInfo,
  IStationDto,
  selectedTableRowsIdsAtomFamily,
  stationsMapBulkPanelOpenAtom,
  tableRowsAtomFamily,
} from "@ryde/shared";

import {
  StationsAreaPolygonsLayer,
  StationsGridMapStyled,
  StationsMapBulkTargetPanel,
  StationsMapControls,
  StationsMapMarkers,
} from "./parts";

export const StationsGridMap: FC = () => {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily("station"));
  const tableRows = useRecoilValue(tableRowsAtomFamily("station"));
  const hasSelection = (selectedIds?.length ?? 0) > 0;
  const isBulkPanelOpen = useRecoilValue(stationsMapBulkPanelOpenAtom);

  const stationsLocations = useMemo(() => {
    if (!selectedIds?.length || !tableRows?.length) {
      return [] as ILocationInfo[];
    }

    const selectedSet = new Set(selectedIds);

    return (tableRows as unknown as IStationDto[])
      .filter(row => selectedSet.has(row.id))
      .map(row => row.metadata?.locationInfo)
      .filter((info): info is ILocationInfo => Boolean(info));
  }, [selectedIds, tableRows]);

  /*
   * Frames the stations picked in the grid. The area-polygons layer runs a second, independent fit of its
   * own, keyed on which areas are shown — the same pair the Leaflet map had, where changing the selection
   * refits to the stations and toggling an area refits to the polygons. They do not collide on load: with
   * no areas shown the polygon fit has no bounds to fit and bails.
   */
  useMapLibreLocationsAutoFit({
    mapRef,
    locations: stationsLocations,
    isMapLoaded,
    resizeEvent: GlobalAppEventEnum.RESIZE_STATIONS_MAP,
  });

  return (
    <StationsGridMapStyled>
      <div className="map_scrollable_content">
        <MapLibreMapHost mapRef={mapRef} onLoadedChange={setIsMapLoaded}>
          <StationsAreaPolygonsLayer
            mapRef={mapRef}
            isMapLoaded={isMapLoaded}
          />
          <StationsMapMarkers />
          <StationsMapControls />
        </MapLibreMapHost>
      </div>

      {isBulkPanelOpen && <StationsMapBulkTargetPanel />}

      {!hasSelection && (
        <div className="empty-state">{t("station.mapEmptySelectionHint")}</div>
      )}
    </StationsGridMapStyled>
  );
};
